import React, { useContext, useEffect, useState } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import CustomTable from "../../../components/CustomTable";
import useEnv from "../../../hooks/env";
import TestReportTemplateApi from "../../../api/services/TestReportTemplateApi";
import { TestReportTemplateVersionResponse } from "../../../api/models/TestReportTemplateVersionResponse";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import ReportTemplateUploadDialog from "./ReportTemplateUploadDialog";
import CustomButton from "../../../components/Button/CustomButton";
import { IFormatRowDataForCustomTableReturn, IReportTemplatesPageProps } from "./interfaces";
import { dashToSlash } from "../../../api/services/common/FormatDateService";
import CustomLoading from "../../../components/Alert/CustomLoading";
import { tesaBlue } from "../../../styles/colors";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    fontFamily: "Proxima Nova",
    margin: theme.spacing(3),
  },
}));

const COLUMN_HEADERS = ["Name of template", "Language", "File type", "Publish date", "Publisher", "Download"];

const ReportTemplatePage: React.FC<IReportTemplatesPageProps> = () => {
  const { api } = useEnv();
  const { handleHttpError } = useContext(ErrorSnackbarContext);

  const testReportTemplateApi = new TestReportTemplateApi(api);

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [rows, setRows] = useState<IFormatRowDataForCustomTableReturn[]>([]);

  const classes = useStyles();

  const downloadReportTemplate = (uuid: string, filename: string) => {
    testReportTemplateApi
      .downloadTestReportTemplate(uuid, filename)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
      })
      .catch(handleHttpError);
  };

  const formatRowDataForCustomTable = (
    rowData: TestReportTemplateVersionResponse[],
  ): IFormatRowDataForCustomTableReturn[] => {
    return rowData?.map((row: TestReportTemplateVersionResponse) => {
      const { uuid, name, language, publishDateTime, publisher, fileType } = row;
      return {
        name,
        language,
        fileType,
        publishDateTime: dashToSlash(publishDateTime as string),
        publisher,
        download: (
          <GetAppIcon
            fontSize="small"
            style={{ color: tesaBlue, cursor: "pointer" }}
            onClick={() => downloadReportTemplate(uuid!, name!)}
          />
        ),
      };
    });
  };

  useEffect(() => {
    setLoading(true);
    testReportTemplateApi
      .getTestReportTemplate()
      .then((response) => {
        setLoading(false);
        const rowData = formatRowDataForCustomTable(response.data as TestReportTemplateVersionResponse[]);
        setRows(rowData);
      })
      .catch(handleHttpError)
      .finally(() => setLoading(false));
  }, []);

  const handleOnClick = () => {
    setOpen((prevState) => !prevState);
  };

  if (loading) {
    return <CustomLoading />;
  }

  return (
    <div className={classes.root}>
      <h2>Report Templates</h2>
      <CustomTable boldIndex={0} columns={COLUMN_HEADERS} headDense rows={rows} />
      <ReportTemplateUploadDialog open={open} onClose={handleOnClick} />
      <CustomButton text="Upload" onClick={handleOnClick} type="button" />
    </div>
  );
};

export default ReportTemplatePage;
